import React, { useState, useEffect } from 'react';
import { Database, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';
import { ConfirmDeleteModal } from '../components/modals/ConfirmDeleteModal';

const STATUS_STYLES = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const SyndicationRequestsAdmin = ({ user }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selected, setSelected] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const isAdmin = user?.role === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.request('/syndication-requests');
      setRequests(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleReview = async () => {
    if (!reviewStatus) { setReviewError('Please select Approve or Reject.'); return; }
    setReviewing(true); setReviewError('');
    try {
      await api.request(`/syndication-requests/${selected.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: reviewStatus, admin_notes: adminNotes }),
      });
      setSelected(null);
      await load();
    } catch (e) { setReviewError(e.message || 'Failed to update.'); }
    setReviewing(false);
  };

  const filtered = requests.filter(r => filterStatus === 'all' || r.status === filterStatus);
  const pendingCount = requests.filter(r => r.status === 'pending').length;

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.ids.length === 1) {
        await api.request(`/syndication-requests/${deleteTarget.ids[0]}`, { method: 'DELETE' });
      } else {
        await api.request('/syndication-requests/bulk-delete', {
          method: 'POST',
          body: JSON.stringify({ ids: deleteTarget.ids }),
        });
      }
      toast.success(`${deleteTarget.ids.length} syndication request(s) moved to Deleted Items.`);
      setSelectedIds(prev => prev.filter(id => !deleteTarget.ids.includes(id)));
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to delete request(s).');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="syndication-requests-admin">
      <div className="bg-levelup-dark rounded-xl p-4 text-white flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Content Syndication Requests</h1>
          <p className="text-gray-300 mt-1">Review and respond to partner requests for LMS content syndication</p>
        </div>
        {pendingCount > 0 && (
          <div className="bg-yellow-400 text-levelup-darker font-bold text-lg px-4 py-2 rounded-xl" data-testid="syndication-pending-badge">
            {pendingCount} Pending
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'approved', 'rejected'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} data-testid={`syndication-filter-${s}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${filterStatus === s ? 'bg-levelup-dark text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {s === 'all' ? `All (${requests.length})` : `${s.charAt(0).toUpperCase() + s.slice(1)} (${requests.filter(r => r.status === s).length})`}
          </button>
        ))}
      </div>

      {isAdmin && selectedIds.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center justify-between" data-testid="syndication-bulk-action-bar">
          <span className="text-sm text-red-700 font-medium">{selectedIds.length} request(s) selected</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedIds([])} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">
              Clear selection
            </button>
            <button
              onClick={() => setDeleteTarget({ ids: selectedIds })}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700"
              data-testid="bulk-delete-syndication-requests-btn"
            >
              <Trash2 size={14} /> Delete Selected
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><div className="animate-spin w-8 h-8 border-4 border-levelup-accent border-t-transparent rounded-full" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-10 text-center text-gray-500">
          <Database size={40} className="mx-auto mb-3 text-gray-300" />
          <p>No {filterStatus !== 'all' ? filterStatus : ''} syndication requests yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(req => (
            <div key={req.id} className="bg-white rounded-xl shadow-md p-5 border border-gray-100 hover:border-levelup-accent/40 transition-all" data-testid={`syndication-row-${req.id}`}>
              <div className="flex items-start justify-between gap-4">
                {isAdmin && (
                  <input
                    type="checkbox"
                    className="mt-1.5"
                    checked={selectedIds.includes(req.id)}
                    onChange={() => toggleSelect(req.id)}
                    data-testid={`select-syndication-request-${req.id}`}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold capitalize ${STATUS_STYLES[req.status] || 'bg-gray-100 text-gray-600'}`}>{req.status}</span>
                    <h3 className="font-bold text-levelup-dark truncate">{req.organization}</h3>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 mb-2">
                    <span><strong>Contact:</strong> {req.name} ({req.email})</span>
                    <span><strong>LMS:</strong> {req.lms_platform}</span>
                    <span><strong>SCORM:</strong> {req.scorm_version}</span>
                    <span><strong>Timeframe:</strong> {req.timeframe}</span>
                    <span><strong>Submitted:</strong> {req.created_at ? req.created_at.substring(0, 10) : '—'}</span>
                  </div>
                  {req.admin_notes && (
                    <p className="text-sm text-blue-700 bg-blue-50 rounded px-2 py-1 mt-2 border-l-2 border-blue-400"><strong>Notes:</strong> {req.admin_notes}</p>
                  )}
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                  <button
                    onClick={() => { setSelected(req); setReviewStatus(req.status !== 'pending' ? req.status : ''); setAdminNotes(req.admin_notes || ''); setReviewError(''); }}
                    className="px-4 py-2 bg-levelup-dark text-white rounded-lg text-sm font-medium hover:bg-levelup-teal transition-colors"
                    data-testid={`syndication-review-btn-${req.id}`}>
                    {req.status === 'pending' ? 'Review' : 'View / Update'}
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => setDeleteTarget({ ids: [req.id] })}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete request"
                      data-testid={`delete-syndication-request-${req.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="syndication-review-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-levelup-dark text-white px-4 py-2.5 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-lg font-bold">Review — {selected.organization}</h2>
              <button onClick={() => setSelected(null)} className="text-gray-300 hover:text-white"><X size={22} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-1">Contact Person</p>
                  <p className="font-semibold">{selected.name}</p>
                  <p className="text-gray-500">{selected.email}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-1">Organisation</p>
                  <p className="font-semibold">{selected.organization}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-1">LMS Platform</p>
                  <p className="font-semibold">{selected.lms_platform}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-500 text-xs mb-1">SCORM Version</p>
                  <p className="font-semibold">{selected.scorm_version}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 md:col-span-2">
                  <p className="text-gray-500 text-xs mb-1">Expected Timeframe</p>
                  <p className="font-semibold">{selected.timeframe}</p>
                </div>
              </div>

              {/* Decision section */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-bold text-gray-700 mb-3">Your Decision</h3>
                <div className="flex gap-3 mb-3">
                  <button onClick={() => setReviewStatus('approved')} data-testid="syndication-approve-btn"
                    className={`flex-1 py-2 rounded-lg font-semibold text-sm border-2 transition-colors ${reviewStatus === 'approved' ? 'bg-green-500 text-white border-green-500' : 'border-green-300 text-green-700 hover:bg-green-50'}`}>
                    Approve
                  </button>
                  <button onClick={() => setReviewStatus('rejected')} data-testid="syndication-reject-btn"
                    className={`flex-1 py-2 rounded-lg font-semibold text-sm border-2 transition-colors ${reviewStatus === 'rejected' ? 'bg-red-500 text-white border-red-500' : 'border-red-300 text-red-700 hover:bg-red-50'}`}>
                    Reject
                  </button>
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (sent to requester)</label>
                  <textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} rows={3}
                    placeholder="Optional: next steps, reason for rejection, or instructions..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-levelup-accent resize-none" />
                </div>
                {reviewError && <p className="text-red-500 text-sm mb-3">{reviewError}</p>}
                <div className="flex gap-3">
                  <button onClick={() => setSelected(null)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                  <button onClick={handleReview} disabled={reviewing} data-testid="syndication-confirm-btn"
                    className="flex-1 py-2 bg-levelup-dark text-white rounded-lg text-sm font-semibold hover:bg-levelup-teal disabled:opacity-60 flex items-center justify-center gap-2">
                    {reviewing ? <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />Saving...</> : 'Confirm Decision'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={!!deleteTarget}
        title="Delete syndication request(s)?"
        itemCount={deleteTarget?.ids.length || 0}
        deleting={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default SyndicationRequestsAdmin;
